/**
 * Which document element a LIVE object names — the `{ ref, address, pathPrefix }`
 * half of a document target; the other half is the property `path`.
 *
 * Lifted out of the project writer so capture, the writer and address caching share
 * ONE implementation — a second copy is how the inspector and the document drifted
 * into two dialects in the first place (plans/one-address-vocabulary.md).
 */

import { createGltfElementAddress, getElementId, type GltfJson, computeElementId, occurrenceOfName, resolveInGraph, type GraphNode, type NeedleAddress } from "@needle-tools/addressing";
import { createDecorator } from "../../../../platform/instantiation/common/registry";
import type { IDisposable } from "../../../../base/common/lifecycle.js";
import { peekReferenceId } from "../../needle/projectReferences";
import { contentNodeAddress } from "./contentContainer";
import { isObject3D } from "../../../../platform/webgl/three.js/helper";
import type { IOpenData } from "./project";

export type OverTarget = { ref: string; address?: NeedleAddress; pathPrefix?: string };

/** the reserved ref naming the COMPOSITION ROOT (the edit scene) — scene-level
 * overs (background, fog) and root parenting target it; it is not a reference
 * entry, the evaluator applies it to whatever it composes into */
export const SCENE_REF = "$scene";

export const IObjectTargetService = createDecorator<IObjectTargetService>("objectTargetService");

/**
 * THE resolution seam, both directions (impl: targetService.ts). The
 * decorator + interface live HERE (the pure types module) so consumers that
 * the service itself depends on (the fold) can import them without a module
 * cycle — one resolver everywhere, never a local mirror.
 */
export interface IObjectTargetService {
    readonly _serviceBrand: undefined;
    registerProvider(provider: IObjectTargetProvider): IDisposable;
    registerCreated?(object: object, parent: OverTarget, referenceId: string): OverTarget | null;

    /** The document element this object names, or null when it names none. */
    targetFor(object: object): OverTarget | null;

    /**
     * The live object a stored target names — the direction a bubble anchor, a
     * stored AI argument or an ADDRESS-CARRYING OPERATION needs. Null when the
     * reference is not loaded or the element is gone; never a plausible wrong
     * match.
     */
    objectFor(target: { ref: string; address?: NeedleAddress }): object | null;
}

export interface IObjectTargetProvider {
    registerCreated?(object: object, parent: OverTarget, referenceId: string): OverTarget | null;
    ownsReference(ref: string): boolean;
    targetFor(object: object): OverTarget | null;
    objectFor(target: { ref: string; address?: NeedleAddress }): object | null;
}

/**
 * A stored target back to the live object — the inverse of {@link documentTargetFor},
 * and what lets an address outlive the session that wrote it (an AI tool argument,
 * a bubble's anchor, a review link).
 *
 * An ADDRESS-LESS target means the reference ROOT, handled first: the root is
 * absent from a loader's associations and `createObjectAddress(root, root)` is
 * null, which silently dropped root-targeted edits three separate times (see
 * sourceAsset.ts). Resolution otherwise runs the addressing ladder, so it lands
 * on the right element or on nothing — never on a plausible wrong one.
 */
export function resolveDocumentTarget(
    target: { ref: string; address?: NeedleAddress },
    deps: { rootFor(ref: string): object | null },
): object | null {
    const root = deps.rootFor(target.ref);
    if (!root) return null;
    if (!target.address) return root;
    return (resolveInGraph(target.address, root as GraphNode) as object | null) ?? null;
}

/** what resolving a reference needs, so the function stays testable and the
 *  caller decides where provenance and the open files come from */
export interface DocumentTargetDeps {
    readonly openFiles: readonly IOpenData[];
    /** the object's provenance stamp — which reference it entered the scene from */
    provenanceOf(object: object): { kind: "user" } | { kind: "asset"; sourceUri: string; referenceId: string } | undefined;
    /** THE content reference's id, minted on first ask */
    contentReferenceId(): string;
    /** the edit scene — what a user-born node's address is walked from */
    compositionRoot(): object | null;
}

/**
 * The full target for a live object: which reference it belongs to, then which
 * element inside it.
 *
 * A user-born node belongs to the content reference, and asking for its address
 * MINTS that id if it does not exist yet. Deliberate: the id is session state
 * that `reset()` clears and nothing persists it, while the alternative — no
 * address until the first save — leaves exactly the objects someone just created
 * (often via the AI) unaddressable, which is the case that matters most.
 */
export function documentTargetFor(object: object, deps: DocumentTargetDeps): OverTarget | null {
    // the REFERENCE-ROOT rule, first (CLAUDE.md): a placed file's root has no
    // address — it is absent from loader associations and a self-walk is null,
    // so anything derived would silently drop. Address-less target = the root.
    for (const file of deps.openFiles) {
        if (file.gltf?.scene !== object) continue;
        const id = peekReferenceId(object);
        return id ? { ref: id } : null;
    }
    // the composition scene: an unstamped Scene that is no open file's root —
    // the reserved $scene ref (scene-level overs). Deliberately NOT an identity
    // check against compositionRoot(): with several hierarchy roots live, "the
    // first root" is not reliably the edit scene, and the equality silently
    // dropped scene-level edits late in a session.
    if ((object as { isScene?: boolean }).isScene === true && !deps.provenanceOf(object)) return { ref: SCENE_REF };
    // an engine COMPONENT carries no stamp of its own — its HOST does; the
    // component branch of buildOverTarget then rides the host's address with a
    // guid-keyed pathPrefix
    const component = object as { guid?: string; gameObject?: object };
    // component hosted on the composition SCENE (#98): the scene carries no
    // provenance stamp, so the stamp walk below can never reach it — it rides
    // the reserved $scene ref like the scene's own edits do
    const host = component.gameObject;
    if (host && (host as { isScene?: boolean }).isScene === true && !deps.provenanceOf(host)) {
        const guid = componentIdentityGuid(object);
        return guid ? { ref: SCENE_REF, pathPrefix: `components/guid:${guid}/` } : null;
    }
    const stampCarrier = typeof component.guid === "string" && component.gameObject && (component.gameObject as { isObject3D?: boolean }).isObject3D === true
        ? component.gameObject
        : object;
    const stamp = deps.provenanceOf(stampCarrier);
    if (!stamp) {
        // file-backed ELEMENT (material/texture): provenance stamps graph nodes,
        // elements are named by the parser ASSOCIATIONS of their home file
        if (!(object as { isObject3D?: boolean }).isObject3D) {
            for (const file of deps.openFiles) {
                const associations = (file.gltf?.parser as { associations?: Map<object, unknown> } | undefined)?.associations;
                if (!associations?.has(object)) continue;
                const scene = file.gltf?.scene;
                const id = scene ? peekReferenceId(scene as object) : undefined;
                return id ? buildOverTarget(object, { id, root: scene as object, open: file }) : null;
            }
            // ANIMATION CLIPS are file elements too, but the loader's
            // associations do not carry them — they hang off `gltf.animations`,
            // a sibling array, which is why the rung above can never find one.
            const clip = animationTargetFor(object, deps.openFiles);
            if (clip) return clip;
        }
        // A MIXER has nothing in the file to point at: it is a runtime object,
        // and its identity is THE ROOT IT DRIVES — durable because the root is.
        const mixerRoot = mixerRootOf(object);
        if (mixerRoot) {
            const id = peekReferenceId(mixerRoot);
            if (id) return { ref: id, address: { type: "mixer" } };
            const root = deps.compositionRoot();
            if (root === mixerRoot) return { ref: deps.contentReferenceId(), address: { type: "mixer" } };
        }
        return null;
    }
    if (stamp.kind === "user") {
        const root = deps.compositionRoot();
        return root ? buildOverTarget(object, { id: deps.contentReferenceId(), root }) : null;
    }
    // the EXACT instance's root first — the same file placed twice shares a uri,
    // and only the stamp's reference id says which copy holds the object (a
    // first-uri pick walked copy ONE's root and returned null for later copies)
    const open = deps.openFiles.find(file => {
        const scene = file.gltf?.scene;
        return !!scene && peekReferenceId(scene as object) === stamp.referenceId;
    }) ?? deps.openFiles.find(file => file.uri.toString(true) === stamp.sourceUri);
    const root = open?.gltf?.scene as object | undefined;
    // the stamped file is not open — nothing to address against, and guessing a
    // different root would produce an address that resolves to the wrong element
    return root ? buildOverTarget(object, { id: stamp.referenceId, root, open }) : null;
}

/** the root an AnimationMixer drives, or null when this is not a mixer */
export function mixerRootOf(object: object): object | null {
    const duck = object as { getRoot?: () => unknown; _root?: unknown; clipAction?: unknown };
    // duck-typed: three is not imported here, and an engine may wrap the mixer
    if (typeof duck.clipAction !== "function") return null;
    const root = typeof duck.getRoot === "function" ? duck.getRoot() : duck._root;
    return root && typeof root === "object" ? root as object : null;
}

/** an AnimationClip addressed against the open file that carries it */
function animationTargetFor(object: object, openFiles: readonly IOpenData[]): OverTarget | null {
    const clip = object as { name?: unknown; duration?: unknown; tracks?: unknown; userData?: Record<string, unknown> };
    // duck-typed for the same reason: tracks + duration IS an AnimationClip
    if (!Array.isArray(clip.tracks) || typeof clip.duration !== "number") return null;
    for (const file of openFiles) {
        const animations = (file.gltf as { animations?: unknown[] } | undefined)?.animations;
        if (!Array.isArray(animations) || !animations.includes(object)) continue;
        const scene = file.gltf?.scene;
        const ref = scene ? peekReferenceId(scene as object) : undefined;
        if (!ref) return null;
        const address: NeedleAddress = { type: "animation" };
        const id = clip.userData?.["needle-id"];
        if (typeof id === "string" && id) address.id = id;
        // NAME is the fallback rung: clips from a file that predates stamping
        // carry no id, and an unaddressable clip is exactly the hole this closes
        if (typeof clip.name === "string" && clip.name) address.name = clip.name;
        return (address.id || address.name) ? { ref, address } : null;
    }
    return null;
}

export function buildOverTarget(owner: object, ref: { id: string; root: object; open?: IOpenData }): OverTarget | null {
    // the reference-root rule, here too: the root addresses as the bare { ref }
    // (a self-walk is null and would silently drop root-targeted edits)
    if (owner === ref.root) return { ref: ref.id };
    // scene-graph node — graph walk, then stamped-id fallback (flat model: loose
    // content children are not under their container root)
    if (isObject3D(owner)) {
        const address = sourceNodeAddress(owner, ref.open) ?? contentNodeAddress(ref.root, owner);
        return address ? { ref: ref.id, address } : null;
    }
    // material: not a graph node — addressed by its stamped id/name
    const material = owner as { isMaterial?: boolean; name?: string; userData?: Record<string, unknown> };
    if (material.isMaterial === true) {
        const address: NeedleAddress = { type: "material" };
        let id = material.userData?.["needle-id"];
        if (typeof id !== "string") {
            // UNNAMED, un-stamped material (editor-created, or from a pre-stamping
            // content glb) — derive the DETERMINISTIC id from the source glTF and
            // stamp it (deriving == stamping per the id contract; the evaluator
            // derives the same id when resolving un-stamped assets — the assign
            // path's recipe). Was: "<object>.color — target is not addressable yet".
            id = deriveMaterialId(material, ref.open);
            if (typeof id === "string") (material.userData ??= {})["needle-id"] = id;
        }
        if (typeof id === "string") address.id = id;
        if (typeof material.name === "string" && material.name.length > 0) address.name = material.name;
        return (address.id || address.name) ? { ref: ref.id, address } : null;
    }
    // engine component: node address + a keyed PATH segment (duck-typed — no engine
    // import needed). The guid is emitted rather than the type name because it is exact:
    // two components of the same type on one node make the readable form ambiguous, and
    // `userData.components` reorders on every add/remove so an index would be worse still.
    const component = owner as { gameObject?: object };
    const componentGuid = componentIdentityGuid(owner);
    if (componentGuid && component.gameObject && isObject3D(component.gameObject)) {
        const address = sourceNodeAddress(component.gameObject, ref.open) ?? contentNodeAddress(ref.root, component.gameObject);
        if (!address) return null;
        return { ref: ref.id, address, pathPrefix: `components/guid:${componentGuid}/` };
    }
    return null;
}

/**
 * A component's STABLE identity for addresses and ops. The runtime `guid` is
 * RE-MINTED on every load (the engine regenerates guids per instance for
 * multi-drop uniqueness), so after a save+reopen two windows DISAGREE on it —
 * a removal/edit naming one window's runtime guid never resolves on the
 * other. The file-baked editor guid (`needle_editor_guid`, written by the
 * export, restored at load) is the shared identity; the runtime guid is the
 * fallback for session-ADDED components (stamped once, becomes the baked
 * guid at the next save). Resolvers (componentByGuid) accept BOTH.
 */
export function componentIdentityGuid(component: object): string | null {
    const c = component as Record<string, unknown>;
    const baked = c["needle_editor_guid"];
    if (typeof baked === "string" && baked.length > 0) return baked;
    const runtime = c["guid"];
    return typeof runtime === "string" && runtime.length > 0 && runtime !== "invalid" ? runtime : null;
}

/** deterministic material id from the owning file's glTF (name + occurrence — stable
 * because the source file is immutable); undefined when the material has no glTF
 * association (scratch materials get RANDOM ids at content birth instead) */
function deriveMaterialId(material: object, open?: IOpenData): string | undefined {
    const parser = open?.gltf?.parser as { json?: { materials?: Array<{ name?: string }> }; associations?: Map<object, { materials?: number }> } | undefined;
    const index = parser?.associations?.get(material)?.materials;
    if (typeof index !== "number" || !parser?.json?.materials) return undefined;
    return computeElementId("material", parser.json.materials[index]?.name ?? (material as { name?: string }).name ?? "", occurrenceOfName(parser.json.materials, index));
}

/** File identity follows the source graph, even after edits move or rename its nodes. */
function sourceNodeAddress(node: object, open: IOpenData | undefined): NeedleAddress | null {
    const parser = open?.gltf?.parser as { json?: GltfJson; associations?: Map<object, { nodes?: number }> } | undefined;
    const json = parser?.json;
    const nodes = json?.nodes;
    if (!json || !Array.isArray(nodes)) return null;
    const liveId = (node as { userData?: Record<string, unknown> }).userData?.["needle-id"];
    let index = parser?.associations?.get(node)?.nodes;
    // Instantiated scenes retain IDs but are absent from the parser's association map.
    if (index === undefined && typeof liveId === "string") {
        index = nodes.findIndex((element, i) =>
            (getElementId(element) ?? computeElementId("node", element.name, occurrenceOfName(nodes, i))) === liveId);
    }
    if (index === undefined || index < 0) return null;
    const address = createGltfElementAddress(json, "node", index);
    if (address && !address.id && typeof liveId === "string") address.id = liveId;
    return address;
}
